import { CONSUMPTION_OPERATIONS, type ConsumptionOperation } from './consumption.js';
import { decAdd, decCmp, decDiv, decMul, decNeg, decSum, formatDec } from './decimal.js';
import { type LedgerEntry, robSummary } from './ledger.js';

/**
 * Voyage Analizi ▸ Operasyon ve Maliyet (ADR 0117 §14, Ek Y4; ortak belgesi §11, ekran 07):
 * dönem stok özeti (başlangıç ROB, alım, tüketim, bitiş ROB), operasyonel döneme ve CP kapsamına
 * göre tüketim tablosu, performans göstergeleri ve yakıt maliyeti. Girdi kesinleşmiş tüketim
 * aralıklarıdır (sefer raporunun bölümü; ADR 0117 §2) ve defter hareketleridir. CP bağımsızdır
 * (T08): CP dışı tüketim ayrı satırdır, toplam iki kez sayılmaz. Aralık bölünmez (oransal dağıtım
 * yok): CP sınırını aşan aralık ayrı "CP sınırı" satırıdır. Saf modül.
 */

/** CP kapsamı: dönem içinde, dışında, sınırda (kısmen) ya da gemide CP kaydı yok. */
export type CpCoverage = 'in' | 'out' | 'partial' | 'none';

export interface AnalysisInterval {
  reportId: string;
  periodStart: string;
  periodEnd: string;
  operation: ConsumptionOperation | string;
  cp: CpCoverage;
  /** Yakıt başına net tüketim (MT). */
  byFuel: Readonly<Record<string, string>>;
  /** Aralığı kapatan raporun mesafesi (nm; seçilen esasa göre); yoksa `null`. */
  distanceNm: number | null;
}

const ms = (value: string | null, open: number) => (value === null ? open : Date.parse(value));

/** Aralığın CP dönemleriyle ilişkisi; dönem yoksa `none`. Uç uca değmek dışarıdır. */
export function cpCoverage(
  start: string,
  end: string,
  periods: readonly { start: string | null; end: string | null }[],
): CpCoverage {
  const live = periods.filter((p) => p.start !== null);
  if (live.length === 0) return 'none';
  const a = Date.parse(start);
  const b = Date.parse(end);
  let partial = false;
  for (const period of live) {
    const pStart = ms(period.start, 0);
    const pEnd = ms(period.end, Number.POSITIVE_INFINITY);
    if (a >= pStart && b <= pEnd) return 'in';
    if (a < pEnd && b > pStart) partial = true;
  }
  return partial ? 'partial' : 'out';
}

export interface OperationRow {
  /** `in:<operasyon>`, `none:<operasyon>`, `partial`, `out`. */
  key: string;
  operation: string | null;
  cp: CpCoverage;
  byFuel: Record<string, string>;
  total: string;
  intervals: number;
}

/** Satır sırası ortağın tasarımındaki gibi (ekran 07): seyir, liman, demir, drift, manevra … */
const ROW_ORDER: readonly ConsumptionOperation[] = [
  'sea',
  'port',
  'anchor',
  'drift',
  'manoeuvring',
  'canal_strait',
  'waiting',
];
const ORDER = (operation: string) => {
  const index = (ROW_ORDER as readonly string[]).indexOf(operation);
  return index < 0 ? CONSUMPTION_OPERATIONS.length : index;
};

/**
 * Operasyonel dönem bazında tüketim (ekran 07 tablosu): CP içi ve CP kaydı olmayan gemide
 * operasyon başına satır; CP dışı bütün operasyonlar tek satır ("CP Dışı"); sınırdaki aralıklar
 * tek satır. Satır toplamları genel toplamla birebir (aynı aralık tek satırda).
 */
export function operationTable(intervals: readonly AnalysisInterval[]): {
  rows: OperationRow[];
  totals: { byFuel: Record<string, string>; total: string };
} {
  const rows = new Map<string, OperationRow>();
  const add = (target: Record<string, string>, fuel: string, value: string) => {
    target[fuel] = formatDec(decAdd(target[fuel] ?? '0', value));
  };
  const totals: Record<string, string> = {};
  for (const item of intervals) {
    const grouped = item.cp === 'in' || item.cp === 'none';
    const key = grouped ? `${item.cp}:${item.operation}` : item.cp;
    const row = rows.get(key) ?? {
      key,
      operation: grouped ? String(item.operation) : null,
      cp: item.cp,
      byFuel: {},
      total: '0',
      intervals: 0,
    };
    for (const [fuel, value] of Object.entries(item.byFuel)) {
      add(row.byFuel, fuel, value);
      add(totals, fuel, value);
    }
    row.total = formatDec(decSum(Object.values(row.byFuel)));
    row.intervals += 1;
    rows.set(key, row);
  }
  const rank = (row: OperationRow) =>
    row.cp === 'in' ? 0 : row.cp === 'none' ? 1 : row.cp === 'partial' ? 2 : 3;
  return {
    rows: [...rows.values()].sort(
      (a, b) => rank(a) - rank(b) || ORDER(a.operation ?? '') - ORDER(b.operation ?? ''),
    ),
    totals: { byFuel: totals, total: formatDec(decSum(Object.values(totals))) },
  };
}

export interface StockSummary {
  startRob: string;
  bunkered: string;
  consumed: string;
  /** Boşaltma, iade ve diğer çıkış (artı MT). */
  otherOut: string;
  /** Onaylı düzeltmeler (işaretli). */
  adjustment: string;
  endRob: string;
}

/**
 * Dönem `[from, to)` stok özeti (ortak belgesi §11 "Stok ve tüketim"): başlangıç = `from`'dan
 * önceki hareketler + dönemde açılan defterin açılışı; bitiş = başlangıç + dönemin hareketleri
 * (transferler gemi toplamında sıfır). `fuelTypeId` verilirse yalnız o yakıt.
 */
export function stockSummary(
  entries: readonly LedgerEntry[],
  window: { from: string; to: string; fuelTypeId?: string | null },
): StockSummary {
  const from = Date.parse(window.from);
  const to = Date.parse(window.to);
  const own = entries.filter((e) => !window.fuelTypeId || e.fuelTypeId === window.fuelTypeId);
  const before = own.filter((e) => Date.parse(e.occurredAt) < from);
  const inside = own.filter((e) => {
    const at = Date.parse(e.occurredAt);
    return at >= from && at < to;
  });
  const sum = robSummary(inside);
  const start = decAdd(decSum(before.map((e) => e.massMt)), sum.opening);
  return {
    startRob: formatDec(start),
    bunkered: sum.bunkered,
    consumed: sum.consumed,
    otherOut: formatDec(decSum([sum.discharged, sum.returned, sum.otherOut])),
    adjustment: sum.adjustment,
    endRob: formatDec(decAdd(start, decSum([sum.closing, decNeg(sum.opening)]))),
  };
}

/** Mesafe gerektiren operasyonlar (eksik mesafe bulgusu). */
const UNDERWAY: ReadonlySet<string> = new Set(['sea', 'manoeuvring', 'canal_strait']);

/**
 * Performans göstergeleri: günlük tüketim = toplam / (aralıkların süresi / 24); mil başına = mesafesi
 * olan aralıkların tüketimi / mesafeleri (limanda tüketim mile bölünmez). Seyir aralığında mesafe
 * yoksa eksik veri sayılır.
 */
export function performanceOf(intervals: readonly AnalysisInterval[]): {
  hours: number;
  dailyMt: string | null;
  perNmMt: string | null;
  distanceNm: number;
  missingDistance: number;
} {
  let hours = 0;
  let distance = 0;
  let missingDistance = 0;
  const totals: string[] = [];
  const distanced: string[] = [];
  for (const item of intervals) {
    hours += (Date.parse(item.periodEnd) - Date.parse(item.periodStart)) / 3_600_000;
    const total = formatDec(decSum(Object.values(item.byFuel)));
    totals.push(total);
    if (item.distanceNm !== null && item.distanceNm > 0) {
      distance += item.distanceNm;
      distanced.push(total);
    } else if (UNDERWAY.has(String(item.operation))) {
      missingDistance += 1;
    }
  }
  const total = decSum(totals);
  const roundedHours = Math.round(hours * 1000) / 1000;
  const roundedDistance = Math.round(distance * 100) / 100;
  return {
    hours: roundedHours,
    dailyMt: hours > 0 ? formatDec(decDiv(decMul(total, '24', 6), String(roundedHours), 2)) : null,
    perNmMt: distance > 0 ? formatDec(decDiv(decSum(distanced), String(roundedDistance), 3)) : null,
    distanceNm: roundedDistance,
    missingDistance,
  };
}

export const COST_METHODS = ['weighted_average', 'batch'] as const;
export type CostMethod = (typeof COST_METHODS)[number];

/**
 * Yakıt maliyeti (ortak belgesi §11 "Maliyet"): ağırlıklı ortalama = yakıtın onaylı alımlarının
 * (kabul × birim maliyet) toplamı / kabul toplamı; parti = tüketim satırının partisinin birim
 * maliyeti. Birim maliyet şirketin para biriminde (alımda kur ve ek masrafla). Fiyatı bilinmeyen
 * tüketim sıfır sayılmaz: `missing` yakıtlarına girer (eksik veri).
 */
export function fuelCost(input: {
  method: CostMethod;
  consumption: readonly { fuelTypeId: string; batchId: string | null; massMt: string }[];
  bunkerings: readonly {
    id: string;
    fuelTypeId: string;
    acceptedMt: string;
    unitCost: string | null;
  }[];
}): { amount: string; missing: string[] } {
  const missing = new Set<string>();
  const average = new Map<string, string>();
  const fuels = new Set(input.bunkerings.map((b) => b.fuelTypeId));
  for (const fuel of fuels) {
    const own = input.bunkerings.filter((b) => b.fuelTypeId === fuel && b.unitCost !== null);
    const quantity = decSum(own.map((b) => b.acceptedMt));
    if (own.length === 0 || decCmp(quantity, '0') <= 0) continue;
    const value = decSum(own.map((b) => decMul(b.acceptedMt, b.unitCost as string, 6)));
    average.set(fuel, formatDec(decDiv(value, quantity, 6)));
  }
  const batches = new Map(input.bunkerings.map((b) => [b.id, b.unitCost]));
  const parts: string[] = [];
  for (const line of input.consumption) {
    if (decCmp(line.massMt, '0') === 0) continue;
    const unit =
      input.method === 'batch'
        ? line.batchId
          ? (batches.get(line.batchId) ?? null)
          : null
        : (average.get(line.fuelTypeId) ?? null);
    if (unit === null) {
      missing.add(line.fuelTypeId);
      continue;
    }
    parts.push(formatDec(decMul(line.massMt, unit, 6)));
  }
  return { amount: formatDec(decDiv(decSum(parts), '1', 2)), missing: [...missing] };
}

/**
 * Raporun bacağı (ekran 05 "Leg"): sefer özetinin aralıklarından (`summarizeVoyage`; ikinci bir
 * durum makinesi yazılmaz). Seyir aralığı kendi bacağıdır (1'den); kalış aralığı (liman, demir,
 * sürüklenme) onu getiren son bacağa yazılır; ilk bacaktan önceki kalış bacaksızdır.
 */
export function legsByReport(
  intervals: readonly { report: { id?: string | null }; leg: number | null }[],
): Map<string, number> {
  const result = new Map<string, number>();
  let last: number | null = null;
  for (const interval of intervals) {
    if (interval.leg !== null) last = interval.leg + 1;
    const id = interval.report.id;
    if (id && last !== null) result.set(id, last);
  }
  return result;
}
